// src/pages/Home/MentionsLegalesPage.jsx
// ou components/MentionsLegales.js selon ton emplacement actuel

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaBalanceScale,
  FaBuilding,
  FaEnvelope,
  FaExternalLinkAlt,
  FaGlobe,
  FaIdCard,
  FaPrint,
  FaShieldAlt,
  FaUserTie,
} from "react-icons/fa";

import useScrollToTop from "../../hooks/useScollToTop";
import { getSettings } from "../../services/settingsServices";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const DEFAULT_SETTINGS = {
  siteweb_club: "",
  nom_club: "",
  adresse_club: "",
  responsable_club: "",
  mail_club: "",
  siret_club: "",
};

const safeValue = (value, fallback = "Non renseigné") => {
  const clean = String(value || "").trim();
  return clean || fallback;
};

const InfoCard = ({ icon: Icon, label, value, href }) => {
  if (!value) return null;

  const content = (
    <div className="group flex h-full items-start gap-4 rounded-3xl border border-slate-200 bg-white/85 p-5 shadow-sm backdrop-blur-xl transition hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg dark:border-white/10 dark:bg-white/5">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary dark:bg-primary/20">
        <Icon />
      </div>

      <div className="min-w-0">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
          {label}
        </p>

        <p className="mt-2 break-words text-sm font-bold leading-6 text-slate-800 dark:text-slate-100">
          {value}
        </p>
      </div>
    </div>
  );

  if (!href) return content;

  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="block h-full">
      {content}
    </a>
  );
};

const LegalSection = ({ number, title, children }) => {
  return (
    <section className="rounded-[1.7rem] border border-slate-200 bg-white/80 p-5 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5 sm:p-6">
      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary text-sm font-extrabold text-white shadow-lg">
          {number}
        </div>

        <div className="min-w-0">
          <h2 className="font-barlow text-xl font-extrabold leading-tight text-slate-950 dark:text-white sm:text-2xl">
            {title}
          </h2>

          <div className="mt-4 space-y-4 text-sm leading-7 text-slate-600 dark:text-slate-300 sm:text-base sm:leading-8">
            {children}
          </div>
        </div>
      </div>
    </section>
  );
};

const MentionsLegalesSkeleton = () => {
  return (
    <main className="min-h-screen bg-slate-50 pt-[calc(var(--site-navbar-h)+48px)] dark:bg-slate-950">
      <div className="mx-auto max-w-6xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mx-auto h-9 w-52 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="mx-auto mt-6 h-12 w-2/3 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
          <div className="mx-auto mt-5 h-5 w-3/4 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className="h-32 animate-pulse rounded-3xl bg-slate-200 dark:bg-slate-800"
            />
          ))}
        </div>

        <div className="mt-10 space-y-5">
          {Array.from({ length: 5 }).map((_, index) => (
            <div
              key={index}
              className="h-36 animate-pulse rounded-[1.7rem] bg-slate-200 dark:bg-slate-800"
            />
          ))}
        </div>
      </div>
    </main>
  );
};

const MentionsLegalesPage = () => {
  useScrollToTop();

  const navigate = useNavigate();

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const data = await getSettings();
        const clubSettings = Array.isArray(data) ? data[0] : data;

        if (!mounted) return;

        setSettings({
          ...DEFAULT_SETTINGS,
          ...(clubSettings || {}),
        });
      } catch (error) {
        console.error("Erreur lors de la récupération des mentions légales :", error);

        if (mounted) {
          setErrorMsg("Impossible de charger les informations du club.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  const siteName = safeValue(settings.siteweb_club, "site internet du club");
  const clubName = safeValue(settings.nom_club, "Le club");
  const clubAddress = safeValue(settings.adresse_club);
  const publicationManager = safeValue(settings.responsable_club);
  const clubEmail = safeValue(settings.mail_club);
  const siret = safeValue(settings.siret_club);

  const siteHref = useMemo(() => {
    const site = String(settings.siteweb_club || "").trim();

    if (!site) return "";

    if (/^https?:\/\//i.test(site)) return site;

    return `https://${site}`;
  }, [settings.siteweb_club]);

  const emailHref = settings.mail_club ? `mailto:${settings.mail_club}` : "";

  const handlePrint = () => {
    window.print();
  };

  if (loading) return <MentionsLegalesSkeleton />;

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-50 pt-[calc(var(--site-navbar-h)+48px)] text-slate-950 dark:bg-slate-950 dark:text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.13),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.14)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.14)_1px,transparent_1px)] bg-[size:42px_42px] opacity-40 dark:opacity-20" />

      <style>{`
        @media print {
          nav,
          footer,
          .no-print {
            display: none !important;
          }

          main {
            padding-top: 0 !important;
            background: #ffffff !important;
          }

          .print-card {
            box-shadow: none !important;
            border: none !important;
          }
        }
      `}</style>

      <div className="relative mx-auto max-w-6xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="no-print mb-8 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-4 py-2.5 text-sm font-bold text-slate-700 shadow-sm backdrop-blur transition hover:-translate-y-0.5 hover:border-primary/30 hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
          >
            <FaArrowLeft className="text-xs" />
            Retour
          </button>

          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:brightness-110"
          >
            <FaPrint className="text-sm" />
            Imprimer
          </button>
        </div>

        <section className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaBalanceScale />
            Textes réglementaires
          </div>

          <h1 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Mentions{" "}
            <span className="text-primary">légales</span>
          </h1>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Informations légales relatives au site internet du club, à son
            responsable de publication, à son hébergement et à l’utilisation des
            services proposés.
          </p>

          <p className="mt-4 text-sm font-bold uppercase tracking-[0.18em] text-slate-400">
            {clubName}
          </p>
        </section>

        {errorMsg && (
          <div className="mt-10 rounded-[1.5rem] border border-red-200 bg-red-50 p-4 text-center text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
            {errorMsg}
          </div>
        )}

        <section className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <InfoCard
            icon={FaBuilding}
            label="Propriétaire"
            value={clubName}
          />

          <InfoCard
            icon={FaGlobe}
            label="Site internet"
            value={siteName}
            href={siteHref}
          />

          <InfoCard
            icon={FaUserTie}
            label="Responsable"
            value={publicationManager}
          />

          <InfoCard
            icon={FaEnvelope}
            label="Contact"
            value={clubEmail}
            href={emailHref}
          />
        </section>

        <article className="print-card mt-10 overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white/90 p-5 shadow-2xl backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/90 sm:p-7 lg:p-8">
          <div className="mb-8 rounded-[2rem] border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <FaIdCard />
                </span>

                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                    Numéro de SIRET
                  </p>
                  <p className="mt-1 text-sm font-bold text-slate-800 dark:text-white">
                    {siret}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <FaShieldAlt />
                </span>

                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                    Hébergement
                  </p>
                  <p className="mt-1 text-sm font-bold text-slate-800 dark:text-white">
                    Hostinger
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-5">
            <LegalSection number="1" title="Informations légales">
              <p>
                En vertu de l’article 6 de la loi n° 2004-575 du 21 juin 2004
                pour la confiance dans l’économie numérique, il est précisé aux
                utilisateurs du site <strong>{siteName}</strong> l’identité des
                différents intervenants dans le cadre de sa réalisation et de son
                suivi.
              </p>

              <ul className="list-disc space-y-2 pl-5">
                <li>
                  <strong>Propriétaire du site :</strong> {clubName} —
                  Association sportive loi 1901 — {clubAddress}
                </li>
                <li>
                  <strong>Responsable publication :</strong>{" "}
                  {publicationManager} — {clubEmail}
                </li>
                <li>
                  <strong>Webmaster :</strong> VICTORIN Laurent —
                  victorinlaurent33@gmail.com
                </li>
                <li>
                  <strong>Hébergeur :</strong> Hostinger
                </li>
                <li>
                  <strong>Numéro de SIRET :</strong> {siret}
                </li>
              </ul>
            </LegalSection>

            <LegalSection
              number="2"
              title="Conditions générales d’utilisation du site et des services proposés"
            >
              <p>
                L’utilisation du site <strong>{siteName}</strong> implique
                l’acceptation pleine et entière des conditions générales
                d’utilisation décrites ci-dessous. Ces conditions d’utilisation
                sont susceptibles d’être modifiées ou complétées à tout moment ;
                les utilisateurs sont donc invités à les consulter régulièrement.
              </p>
            </LegalSection>

            <LegalSection number="3" title="Description des services fournis">
              <p>
                Le site <strong>{siteName}</strong> a pour objet de fournir une
                information concernant l’ensemble des activités de l’association.
                Le propriétaire du site s’efforce de fournir des informations
                aussi précises que possible. Toutefois, il ne pourra être tenu
                responsable des omissions, inexactitudes ou carences dans la mise
                à jour.
              </p>
            </LegalSection>

            <LegalSection
              number="4"
              title="Propriété intellectuelle et contrefaçons"
            >
              <p>
                <strong>{clubName}</strong> est propriétaire des droits de
                propriété intellectuelle ou détient les droits d’usage sur les
                éléments accessibles sur le site, notamment les textes, images,
                graphismes, logos, icônes, sons et logiciels.
              </p>

              <p>
                Toute reproduction, représentation, modification, publication ou
                adaptation de tout ou partie des éléments du site est interdite,
                sauf autorisation écrite préalable de <strong>{clubName}</strong>.
                Toute exploitation non autorisée pourra être considérée comme
                constitutive d’une contrefaçon.
              </p>
            </LegalSection>

            <LegalSection number="5" title="Limitations de responsabilité">
              <p>
                <strong>{clubName}</strong> ne pourra être tenue responsable des
                dommages directs ou indirects causés au matériel de l’utilisateur
                lors de l’accès au site <strong>{siteName}</strong>, notamment en
                cas de bug, incompatibilité ou utilisation d’un matériel non
                adapté.
              </p>
            </LegalSection>

            <LegalSection number="6" title="Gestion des données personnelles">
              <p>
                En France, les données personnelles sont notamment protégées par
                la loi n° 78-17 du 6 janvier 1978 relative à l’informatique, aux
                fichiers et aux libertés, ainsi que par les dispositions
                applicables en matière de protection des données.
              </p>

              <p>
                À l’occasion de l’utilisation du site <strong>{siteName}</strong>,
                certaines informations peuvent être recueillies pour le besoin
                des services proposés. L’utilisateur fournit ces informations en
                toute connaissance de cause lorsqu’il procède lui-même à leur
                saisie.
              </p>

              <p>
                Tout utilisateur dispose d’un droit d’accès, de rectification et
                d’opposition aux données personnelles le concernant, en effectuant
                sa demande auprès du responsable de publication.
              </p>
            </LegalSection>

            <LegalSection number="7" title="Liens hypertextes et cookies">
              <p>
                Le site <strong>{siteName}</strong> peut contenir des liens
                hypertextes vers d’autres sites. <strong>{clubName}</strong> n’a
                pas la possibilité de vérifier le contenu des sites ainsi visités
                et n’assumera aucune responsabilité de ce fait.
              </p>

              <p>
                La navigation sur le site peut provoquer l’installation de
                cookies sur l’ordinateur de l’utilisateur. Un cookie est un
                fichier de petite taille qui ne permet pas l’identification de
                l’utilisateur, mais enregistre des informations relatives à la
                navigation.
              </p>
            </LegalSection>
          </div>

          <div
            aria-hidden
            className="mt-8 h-1.5 w-full rounded-full bg-gradient-to-r from-transparent via-primary to-transparent opacity-90"
          />
        </article>
      </div>
    </main>
  );
};

export default MentionsLegalesPage;